import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useHouseholdId } from '../hooks/useHouseholdId'
import { useHouseholdRealtime } from '../hooks/useHouseholdRealtime'
import { useCurrentShoppingList } from '../hooks/useCurrentShoppingList'
import { useLastShopSummary } from '../hooks/useLastShopSummary'
import { useUsualSuspects } from '../hooks/useUsualSuspects'
import { useSuggestedList } from '../hooks/useSuggestedList'
import { useAcceptSuggestedList } from '../hooks/useAcceptSuggestedList'
import { ShopMasthead } from '../components/shop/ShopMasthead'
import { CurrentListCard } from '../components/shop/CurrentListCard'
import { SuggestedListCard } from '../components/shop/SuggestedListCard'
import { EmptyShopState } from '../components/shop/EmptyShopState'
import { LastShopCard } from '../components/shop/LastShopCard'
import { UsualSuspects } from '../components/shop/UsualSuspects'
import type { UsualSuspect } from '../hooks/useUsualSuspects'

export default function Shop() {
  const navigate    = useNavigate()
  const householdId = useHouseholdId()

  const { list,    refresh: refreshList }    = useCurrentShoppingList(householdId)
  const { summary: lastShop }                = useLastShopSummary(householdId)
  const { suspects }                         = useUsualSuspects(householdId)
  const suggested                            = useSuggestedList()
  const { accept, loading: accepting }       = useAcceptSuggestedList(householdId)

  useHouseholdRealtime(householdId, {
    tables: ['list_items', 'shopping_lists'],
    invalidateMap: {
      list_items:     [['listItems'], ['list-items'], ['active-list', householdId], ['latestList', householdId]],
      shopping_lists: [['latestList', householdId], ['active-list', householdId], ['shopping-lists', householdId], ['latestListMeta', householdId]],
    },
  })

  const hasCurrent       = list !== null
  const hasSuggestion    = !hasCurrent && suggested.length > 0

  async function handleAcceptSuggestion() {
    const id = await accept(suggested)
    if (id) { refreshList(); navigate('/list') }
  }

  async function handleCreateManual() {
    if (!householdId) return
    const { data: newList } = await supabase
      .from('shopping_lists')
      .insert({ household_id: householdId, generated_at: new Date().toISOString() })
      .select('id')
      .single()
    if (newList) { refreshList(); navigate('/list') }
  }

  async function handleAddSuspect(suspect: UsualSuspect) {
    if (!householdId) return

    if (hasCurrent && list) {
      await supabase.from('list_items').insert({
        list_id:       list.id,
        item_id:       suspect.itemId,
        status:        'pending',
        urgency_score: 50,
        sort_order:    0,
        is_one_off:    false,
      })
      refreshList()
    } else {
      await accept([{ itemId: suspect.itemId, name: suspect.name, reason: 'out' }])
      refreshList()
    }
  }

  return (
    <main className="min-h-screen bg-cream px-4 pt-8 pb-28">

      <ShopMasthead
        currentListCount={list?.itemCount ?? 0}
        hasSuggestion={hasSuggestion}
      />

      {hasCurrent ? (
        <CurrentListCard
          list={list!}
          onStartShopping={() => navigate('/list')}
          onViewList={() => navigate('/list')}
        />
      ) : hasSuggestion ? (
        <SuggestedListCard
          items={suggested}
          loading={accepting}
          onStartList={handleAcceptSuggestion}
        />
      ) : (
        <EmptyShopState onCreateManual={handleCreateManual} />
      )}

      {lastShop && <LastShopCard summary={lastShop} />}

      <UsualSuspects suspects={suspects} onAdd={handleAddSuspect} />

    </main>
  )
}
